import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/admin/DashboardLayout';
import RequireAdmin from '../components/admin/RequireAdmin';
import { supabase } from '../supabaseClient';
import { Activity, RefreshCw, User, Globe, Laptop, MapPin } from 'lucide-react';

interface UserActivityLog {
    id: string;
    user_id: string | null;
    username: string | null;
    user_type: string;
    ip_address: string;
    city: string;
    region: string;
    country: string;
    browser: string;
    os: string;
    device_type: string;
    route: string;
    feature_name: string;
    created_at: string;
}

export default function UserActivityPage() {
    const [logs, setLogs] = useState<UserActivityLog[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchLogs = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('user_activity_logs')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(500);

        if (error) {
            console.error('Error fetching user activity logs:', error);
        } else if (data) {
            setLogs(data);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleString('id-ID', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getDeviceIcon = (deviceType: string) => {
        if (deviceType?.toLowerCase().includes('mobile')) return <Globe size={16} />;
        return <Laptop size={16} />;
    };

    return (
        <RequireAdmin>
            <DashboardLayout>
                <div className="p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-3">
                                <Activity size={28} className="text-indigo-400" /> User Activity Logs
                            </h1>
                            <p className="text-slate-400 mt-1">Lacak interaksi user, lokasi, dan device secara aman tanpa mengekspos database.</p>
                        </div>
                        <button
                            onClick={fetchLogs}
                            className="flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition shadow-lg"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                            Refresh Data
                        </button>
                    </div>

                    <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden shadow-xl">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-[10px] font-black tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Waktu</th>
                                        <th className="px-6 py-4">Tipe User</th>
                                        <th className="px-6 py-4">Aktivitas</th>
                                        <th className="px-6 py-4">Lokasi & IP</th>
                                        <th className="px-6 py-4">Device Info</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                                <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                                                Memuat data log aktivitas...
                                            </td>
                                        </tr>
                                    ) : logs.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                                Belum ada data aktivitas tercatat.
                                            </td>
                                        </tr>
                                    ) : (
                                        logs.map((log) => (
                                            <tr key={log.id} className="hover:bg-slate-700/30 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {formatDate(log.created_at)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${log.user_type === 'user' ? 'bg-indigo-900/50 text-indigo-400' : 'bg-slate-700 text-slate-300'}`}>
                                                        <User size={12} />
                                                        {log.user_type === 'user' ? `Logged In (${log.username || 'User'})` : 'Guest'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="font-bold text-white">{log.feature_name}</p>
                                                    <p className="text-xs text-slate-500 mt-0.5">Router: <code className="text-indigo-300">{log.route}</code></p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-start gap-2">
                                                        <MapPin size={14} className="text-slate-500 mt-0.5" />
                                                        <div>
                                                            <p className="text-slate-300">
                                                                {log.city !== 'unknown' ? `${log.city}, ${log.country}` : 'Lokasi Tersembunyi'}
                                                            </p>
                                                            <p className="text-[10px] text-slate-500 font-mono mt-0.5">IP: {log.ip_address}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        {getDeviceIcon(log.device_type)}
                                                        <div>
                                                            <p className="text-slate-300">{log.os} • {log.browser}</p>
                                                            <p className="text-[10px] text-slate-500 capitalize">{log.device_type || 'Desktop/Unknown'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        </RequireAdmin>
    );
}
